import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { content } from "../i18n/content";
import { scrollToId } from "../lib/scroll";

const Chapter = ({ item, index }) => {
  const num = String(index + 1).padStart(2, "0");

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay: 0.05 * index, ease: [0.16, 1, 0.3, 1] }}
      className="group relative border-t border-[#f9f6f0]/12 py-12 sm:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start"
      data-testid={`service-chapter-${item.id}`}
    >
      <div className="lg:col-span-4">
        <span className="font-display italic text-5xl sm:text-6xl text-[#c86f53] font-light transition-transform duration-700 inline-block group-hover:-translate-y-1">
          {num}
        </span>
        <h3 className="mt-5 font-display text-3xl sm:text-4xl leading-tight tracking-tight text-[#f9f6f0] font-medium" data-testid={`service-title-${item.id}`}>
          {item.titleDe}
        </h3>
        <p className="font-display italic text-xl sm:text-2xl text-[#c86f53]/90 font-light">{item.titleEn}</p>
        <p className="mt-5 text-sm text-[#d4cfc7] font-light leading-relaxed max-w-xs">{item.taglineDe}</p>
        <p className="mt-1.5 font-display italic text-base text-[#d4cfc7]/60 font-light leading-relaxed max-w-xs">
          {item.taglineEn}
        </p>
        <button
          onClick={() => scrollToId("contact")}
          className="mt-7 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-[#f9f6f0]/80 hover:text-[#c86f53] transition-colors duration-300"
          data-testid={`service-cta-${item.id}`}
        >
          {content.services.enquire}
          <span className="w-9 h-9 rounded-full border border-[#f9f6f0]/25 flex items-center justify-center transition-colors duration-500 group-hover:bg-[#c86f53] group-hover:border-[#c86f53] group-hover:text-[#f9f6f0]">
            <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:rotate-45" />
          </span>
        </button>
      </div>

      <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#c86f53] mb-6 border-b border-[#f9f6f0]/10 pb-3">
            {content.services.colDe}
          </p>
          <ul className="space-y-4">
            {item.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-4 text-sm sm:text-[15px] leading-relaxed text-[#f9f6f0]/90 font-light">
                <Check size={15} className="mt-1 shrink-0 text-[#c86f53]" strokeWidth={1.5} />
                <span>{b.de}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#6b705c] mb-6 border-b border-[#f9f6f0]/10 pb-3" style={{ color: "#9aa086" }}>
            {content.services.colEn}
          </p>
          <ul className="space-y-4">
            {item.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-4 text-sm sm:text-[15px] leading-relaxed text-[#d4cfc7]/70 font-light">
                <Check size={15} className="mt-1 shrink-0 text-[#6b705c]" strokeWidth={1.5} />
                <span className="font-display italic text-base sm:text-lg">{b.en}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="services" className="relative bg-[#1e1b1b] py-28 sm:py-40" data-testid="services-section">
    <div className="max-w-[1500px] mx-auto px-6 sm:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20 sm:mb-28 items-end">
        <div className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-[11px] uppercase tracking-[0.35em] text-[#c86f53] mb-8"
            data-testid="services-chapter-label"
          >
            {content.services.chapter}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#f9f6f0] font-medium max-w-3xl"
            data-testid="services-heading"
          >
            {content.services.headingDe}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-4 font-display italic text-2xl sm:text-3xl text-[#c86f53]/90 font-light"
            data-testid="services-heading-en"
          >
            {content.services.headingEn}
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="lg:col-span-4"
          data-testid="services-subtext"
        >
          <p className="text-sm sm:text-base leading-relaxed text-[#d4cfc7]/85 font-light">{content.services.subDe}</p>
          <p className="mt-2 font-display italic text-base sm:text-lg leading-relaxed text-[#d4cfc7]/55 font-light">
            {content.services.subEn}
          </p>
        </motion.div>
      </div>

      <div>
        {content.services.items.map((item, i) => (
          <Chapter key={item.id} item={item} index={i} />
        ))}
        <div className="border-t border-[#f9f6f0]/12" />
      </div>
    </div>
  </section>
);
